import type { PrimerQuestion, CTA, QuestionLabel } from '../../lib/tenant-api';

export interface PrimerQuestionForm {
  question: string;
  answer: string;
  parent_ids: string[];
  is_active: boolean;
  is_root: boolean;
  is_conversion: boolean;
  is_optimizable: boolean;
  is_first_class: boolean;
  order_index: number;
  asset_selection_mode: 'script' | 'tags';
  asset_tags: string[];
  is_faq: boolean;
  question_type: 'primer' | 'faq';
  answer_summary: string | null;
}

export interface FollowUpSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (questionIds: string[]) => void;
  availableQuestions: PrimerQuestion[];
  currentFollowUps: string[];
  parentQuestion: PrimerQuestion;
}

export interface CTASelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (ctaIds: string[]) => void;
  availableCTAs: CTA[];
  currentCTAs: string[];
  question: PrimerQuestion;
}

export interface ParentRemovalSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onRemove: (parentIds: string[]) => void;
  question: PrimerQuestion;
  allQuestions: PrimerQuestion[];
}

export interface TreeViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: PrimerQuestion[];
  getFollowUpQuestions: (parentId: string) => PrimerQuestion[];
  questionCTAs: Record<string, CTA[]>;
  onEditQuestion: (question: PrimerQuestion) => void;
}

export interface FlowNodeProps {
  question: PrimerQuestion;
  level: number;
  hierarchicalNumber: string;
  getFollowUpQuestions: (parentId: string) => PrimerQuestion[];
  questionCTAs: Record<string, CTA[]>;
  expandedNodes: Set<string>;
  onToggle: (questionId: string) => void;
  visitedPath?: Set<string>;
  onEditQuestion: (question: PrimerQuestion) => void;
}

export interface FollowUpQuestionItemProps {
  question: PrimerQuestion;
  level?: number;
  parentNumber?: string;
  index?: number;
  visitedPath?: Set<string>;
  allQuestions?: PrimerQuestion[];
  onDisplayFollowUps: (questionId: string) => void;
  onManageCTAs: (question: PrimerQuestion) => void;
  onAddFollowUp: (question: PrimerQuestion) => void;
  onEdit: (question: PrimerQuestion) => void;
  onToggleActive: (question: PrimerQuestion) => void;
  onDelete: (questionId: string) => void;
  onRemoveCTA: (questionId: string, ctaId: string) => void;
  getFollowUpQuestions: (parentId: string) => PrimerQuestion[];
  questionCTAs: Record<string, CTA[]>;
  displayFollowUps: Set<string>;
  availableLabels: QuestionLabel[];
  onCreateLabel: (name: string) => Promise<QuestionLabel | null>;
  onAssignLabels: (questionId: string, labelIds: string[]) => Promise<void>;
  getCTATypeColor: (type: string) => string;
  fetchQuestions: () => Promise<void>;
}

export interface QuestionItemProps {
  question: PrimerQuestion;
  level?: number;
  visitedPath?: Set<string>;
  allQuestions?: PrimerQuestion[];
  hierarchyPath?: string[];
  index?: number;
  // Injected dependencies (from parent)
  expandedCards: Set<string>;
  setExpandedCards: React.Dispatch<React.SetStateAction<Set<string>>>;
  displayFollowUps: Set<string>;
  setDisplayFollowUps: React.Dispatch<React.SetStateAction<Set<string>>>;
  questionCTAs: Record<string, CTA[]>;
  getFollowUpQuestions: (parentId: string) => PrimerQuestion[];
  handleManageCTAs: (question: PrimerQuestion) => void;
  handleAddFollowUp: (question: PrimerQuestion) => void;
  handleEdit: (question: PrimerQuestion) => void;
  handleToggleActive: (question: PrimerQuestion) => void;
  handleDelete: (questionId: string) => void;
  handleRemoveCTA: (questionId: string, ctaId: string) => void;
  setShowTreeView: (show: boolean) => void;
  availableLabels: QuestionLabel[];
  handleCreateLabel: (name: string) => Promise<QuestionLabel | null>;
  handleAssignLabels: (questionId: string, labelIds: string[]) => Promise<void>;
  getCTATypeColor: (type: string) => string;
  fetchQuestions: () => Promise<void>;
  handleMoveUp?: (question: PrimerQuestion) => Promise<void>;
  handleMoveDown?: (question: PrimerQuestion) => Promise<void>;
  /** Opens the per-question performance view (score trend + answer history). */
  handleViewPerformance?: (question: PrimerQuestion) => void;
  /** Total number of sibling questions at the same level/list — used to disable ↑/↓ at boundaries */
  totalSiblings?: number;
  dropMenuMobilePos?: string;
}

// Constants for safety limits
export const MAX_DEPTH = 8;
export const MAX_NODES_PER_LEVEL = 25;

// Utility function to parse markdown images from answer text
export const parseImagesFromMarkdown = (markdown: string): Array<{ url: string; alt: string }> => {
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const images: Array<{ url: string; alt: string }> = [];
  let match;

  while ((match = imageRegex.exec(markdown)) !== null) {
    images.push({
      alt: match[1] || '',
      url: match[2]
    });
  }

  return images;
};

export const getCTATypeLabel = (type: string) => {
  const types: Record<string, string> = {
    generic: 'Generic Button',
    link: 'Link Button',
    form_email: 'Email Form',
    form_phone: 'Phone Form',
    form_name: 'Name Form',
    form_full: 'Full Details Form'
  };
  return types[type] || type;
};

export const getCTATypeColor = (type: string) => {
  const colors: Record<string, string> = {
    generic: 'bg-indigo-100 text-indigo-800',
    link: 'bg-green-100 text-green-800',
    form_email: 'bg-purple-100 text-purple-800',
    form_phone: 'bg-orange-100 text-orange-800',
    form_name: 'bg-pink-100 text-pink-800',
    form_full: 'bg-red-100 text-red-800'
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

export const INITIAL_FORM_DATA: PrimerQuestionForm = {
  question: '',
  answer: '',
  parent_ids: [],
  is_active: true,
  is_root: true,
  is_conversion: false,
  is_optimizable: true,
  is_first_class: false,
  order_index: 0,
  asset_selection_mode: 'script',
  asset_tags: [],
  is_faq: false,
  question_type: 'primer',
  answer_summary: null
};
